import { useState, useEffect } from "react";
import { supabase } from "./lib/supabase";

// ============================================================
// WEDYRA — Système d'avis et notes
// Couples : laisser un avis | Pros : voir & répondre
// ============================================================

function Stars({ count, size = 18, interactive = false, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: "flex", gap: 2, cursor: interactive ? "pointer" : "default" }}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i}
          style={{ fontSize: size, transition: "transform 0.15s", transform: (interactive && hover === i) ? "scale(1.2)" : "scale(1)" }}
          onMouseEnter={() => interactive && setHover(i)}
          onMouseLeave={() => interactive && setHover(0)}
          onClick={() => interactive && onChange?.(i)}>
          {i <= (hover || count) ? "★" : "☆"}
        </span>
      ))}
    </div>
  );
}

function TimeAgo({ date }) {
  if (!date) return null;
  const d = new Date(date);
  const now = new Date();
  const diff = Math.floor((now - d) / 86400000);
  let text;
  if (diff === 0) text = "Aujourd'hui";
  else if (diff === 1) text = "Hier";
  else if (diff < 7) text = `Il y a ${diff} jours`;
  else if (diff < 30) text = `Il y a ${Math.floor(diff / 7)} sem.`;
  else if (diff < 365) text = `Il y a ${Math.floor(diff / 30)} mois`;
  else text = d.toLocaleDateString("fr-FR");
  return <span>{text}</span>;
}

// ============================================================
// AVIS CÔTÉ COUPLE — Laisser un avis
// ============================================================
export function CoupleAvis({ user, C }) {
  const [avis, setAvis] = useState([]);
  const [prestas, setPrestas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedPresta, setSelectedPresta] = useState(null);
  const [form, setForm] = useState({ note: 0, titre: "", commentaire: "", recommande: true });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const card = { background: C.card, borderRadius: 18, padding: "22px 24px", border: `1px solid ${C.cardBorder}`, marginBottom: 16 };

  useEffect(() => { loadData(); }, [user.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les avis déjà laissés
      if (user.id) {
        const { data: myAvis } = await supabase
          .from("avis_prestataires")
          .select("*, prestataires(nom_entreprise, ville)")
          .eq("couple_id", user.id)
          .order("created_at", { ascending: false });
        if (myAvis) setAvis(myAvis);

        // Charger les prestataires contactés (pour lesquels on peut laisser un avis)
        const { data: contacts } = await supabase
          .from("demandes_contact")
          .select("prestataire_id, prestataires(id, nom_entreprise, ville, note_moyenne, nb_avis)")
          .eq("couple_id", user.id)
          .eq("statut", "acceptee");
        if (contacts) {
          const already = (myAvis || []).map(a => a.prestataire_id);
          setPrestas(contacts.filter(c => !already.includes(c.prestataire_id)).map(c => c.prestataires));
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const submitAvis = async () => {
    if (!form.note || !form.commentaire.trim() || !selectedPresta) return;
    setSubmitting(true);
    try {
      await supabase.from("avis_prestataires").insert({
        couple_id: user.id || null,
        prestataire_id: selectedPresta.id,
        couple_name: user.nom,
        note: form.note,
        titre: form.titre.trim(),
        commentaire: form.commentaire.trim(),
        recommande: form.recommande,
        date_mariage: user.date || null,
      });
      setSubmitted(true);
      setTimeout(() => { setSubmitted(false); setShowForm(false); setForm({ note: 0, titre: "", commentaire: "", recommande: true }); setSelectedPresta(null); loadData(); }, 2500);
    } catch (e) { console.error(e); }
    setSubmitting(false);
  };

  if (loading) return <div style={{ ...card, textAlign: "center", padding: "40px" }}><div style={{ color: C.textLight }}>Chargement...</div></div>;

  return (
    <div>
      {/* LAISSER UN AVIS */}
      {!showForm && prestas.length > 0 && (
        <div style={{ ...card, background: C.primaryBg, border: `1px solid ${C.primaryBorder}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: C.primary, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 4, fontWeight: 600 }}>⭐ Partagez votre expérience</div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: C.text }}>{prestas.length} prestataire{prestas.length > 1 ? "s" : ""} en attente d'avis</div>
            </div>
            <button onClick={() => setShowForm(true)} style={{ background: C.primary, color: "#fff", border: "none", borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Laisser un avis →</button>
          </div>
        </div>
      )}

      {/* FORMULAIRE */}
      {showForm && !submitted && (
        <div style={card}>
          <button onClick={() => { setShowForm(false); setSelectedPresta(null); }} style={{ background: "none", border: "none", color: C.primary, fontSize: 13, cursor: "pointer", fontWeight: 600, marginBottom: 16 }}>← Retour</button>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: C.text, marginBottom: 20 }}>Laisser un avis</div>

          {/* Sélection prestataire */}
          {!selectedPresta ? (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 10 }}>Choisir le prestataire :</div>
              <div style={{ display: "grid", gap: 10 }}>
                {prestas.map(p => (
                  <div key={p.id} onClick={() => setSelectedPresta(p)}
                    style={{ padding: "14px 18px", borderRadius: 14, border: `1.5px solid ${C.cardBorder}`, cursor: "pointer", display: "flex", alignItems: "center", gap: 14, transition: "all 0.2s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = C.primary}
                    onMouseLeave={e => e.currentTarget.style.borderColor = C.cardBorder}>
                    <div style={{ width: 42, height: 42, borderRadius: 12, background: C.primaryBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🏢</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{p.nom_entreprise}</div>
                      <div style={{ fontSize: 12, color: C.textLight }}>📍 {p.ville || "Alsace"}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              {/* Prestataire sélectionné */}
              <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", background: C.primaryBg, borderRadius: 12, marginBottom: 20 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: C.primary + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🏢</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{selectedPresta.nom_entreprise}</div>
                  <div style={{ fontSize: 12, color: C.textLight }}>{selectedPresta.ville}</div>
                </div>
                <button onClick={() => setSelectedPresta(null)} style={{ background: "none", border: "none", color: C.textLight, cursor: "pointer", fontSize: 12 }}>Changer</button>
              </div>

              {/* Note */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 }}>Note globale *</div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Stars count={form.note} size={32} interactive onChange={n => setForm(f => ({ ...f, note: n }))} />
                  <span style={{ fontSize: 14, color: C.textLight }}>
                    {form.note === 1 && "Décevant"}{form.note === 2 && "Moyen"}{form.note === 3 && "Bien"}{form.note === 4 && "Très bien"}{form.note === 5 && "Excellent !"}
                  </span>
                </div>
              </div>

              {/* Titre */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>Titre de l'avis</div>
                <input value={form.titre} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))} placeholder="Ex: Photographe incroyable, merci !"
                  style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: `1.5px solid ${C.cardBorder}`, background: C.card, color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box" }}
                  onFocus={e => e.target.style.borderColor = C.primary} onBlur={e => e.target.style.borderColor = C.cardBorder} />
              </div>

              {/* Commentaire */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>Votre avis * <span style={{ fontWeight: 400, color: C.textLight }}>({form.commentaire.length}/500)</span></div>
                <textarea value={form.commentaire} onChange={e => setForm(f => ({ ...f, commentaire: e.target.value.slice(0, 500) }))} placeholder="Partagez votre expérience : qualité du service, professionnalisme, rapport qualité-prix, communication..."
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: `1.5px solid ${C.cardBorder}`, background: C.card, color: C.text, fontSize: 14, outline: "none", resize: "vertical", minHeight: 100, boxSizing: "border-box", lineHeight: 1.6 }} />
              </div>

              {/* Recommandation */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 }}>Recommanderiez-vous ce prestataire ?</div>
                <div style={{ display: "flex", gap: 10 }}>
                  {[{ v: true, l: "👍 Oui, absolument" }, { v: false, l: "👎 Non" }].map(o => (
                    <div key={o.l} onClick={() => setForm(f => ({ ...f, recommande: o.v }))}
                      style={{ padding: "10px 18px", borderRadius: 10, border: `1.5px solid ${form.recommande === o.v ? (o.v ? C.green : C.primary) : C.cardBorder}`, background: form.recommande === o.v ? (o.v ? C.greenBg : C.primaryBg) : C.card, color: form.recommande === o.v ? (o.v ? C.green : C.primary) : C.text, cursor: "pointer", fontSize: 13, fontWeight: form.recommande === o.v ? 600 : 400, transition: "all 0.2s" }}>
                      {o.l}
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <button onClick={submitAvis} disabled={!form.note || !form.commentaire.trim() || submitting}
                style={{ width: "100%", padding: "14px", background: (!form.note || !form.commentaire.trim()) ? C.cardBorder : C.primary, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: (!form.note || !form.commentaire.trim()) ? "default" : "pointer", transition: "all 0.3s" }}>
                {submitting ? "Envoi en cours..." : "Publier mon avis →"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* SUCCÈS */}
      {submitted && (
        <div style={{ ...card, textAlign: "center", padding: "48px 36px" }}>
          <div style={{ fontSize: 48, marginBottom: 14 }}>🎉</div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: C.text, marginBottom: 8 }}>Merci pour votre avis !</div>
          <p style={{ fontSize: 14, color: C.textLight }}>Votre retour aide les futurs mariés à faire le bon choix.</p>
        </div>
      )}

      {/* MES AVIS */}
      {avis.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: 11, color: C.textLight, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 18, fontWeight: 600 }}>Mes avis publiés ({avis.length})</div>
          {avis.map(a => (
            <div key={a.id} style={{ padding: "16px 0", borderBottom: `1px solid ${C.cardBorder}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, color: C.text }}>{a.prestataires?.nom_entreprise || "Prestataire"}</div>
                  <div style={{ fontSize: 12, color: C.textLight, marginTop: 2 }}>📍 {a.prestataires?.ville || "Alsace"}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Stars count={a.note} size={14} />
                  <span style={{ fontSize: 12, color: C.textLight }}><TimeAgo date={a.created_at} /></span>
                </div>
              </div>
              {a.titre && <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>{a.titre}</div>}
              <p style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.6 }}>{a.commentaire}</p>
              {a.recommande !== null && <div style={{ marginTop: 8, fontSize: 12, color: a.recommande ? C.green : C.primary }}>{a.recommande ? "👍 Recommandé" : "👎 Non recommandé"}</div>}
              {a.reponse_pro && (
                <div style={{ marginTop: 10, padding: "10px 14px", background: C.notesBg, borderRadius: 10, borderLeft: `3px solid ${C.primary}` }}>
                  <div style={{ fontSize: 11, color: C.primary, fontWeight: 600, marginBottom: 4 }}>Réponse du prestataire</div>
                  <p style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{a.reponse_pro}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* AUCUN AVIS & AUCUN PRESTA À NOTER */}
      {!showForm && prestas.length === 0 && avis.length === 0 && (
        <div style={{ ...card, textAlign: "center", padding: "60px 40px" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⭐</div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: C.text, marginBottom: 10 }}>Aucun avis pour l'instant</div>
          <p style={{ fontSize: 14, color: C.textLight, maxWidth: 400, margin: "0 auto" }}>Contactez des prestataires via le Matching, puis laissez-leur un avis après votre échange.</p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// AVIS CÔTÉ PRO — Voir et répondre aux avis
// ============================================================
export function ProAvis({ user, C }) {
  const [avis, setAvis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [stats, setStats] = useState({ avg: 0, total: 0, recommande: 0, distribution: [0, 0, 0, 0, 0] });

  const card = { background: C.card, borderRadius: 18, padding: "22px 24px", border: `1px solid ${C.cardBorder}`, marginBottom: 16 };

  useEffect(() => { loadAvis(); }, [user.id]);

  const loadAvis = async () => {
    setLoading(true);
    try {
      if (user.id) {
        const { data } = await supabase
          .from("avis_prestataires")
          .select("*")
          .eq("prestataire_id", user.id)
          .eq("is_visible", true)
          .order("created_at", { ascending: false });
        if (data) {
          setAvis(data);
          const total = data.length;
          const avg = total > 0 ? (data.reduce((s, a) => s + a.note, 0) / total).toFixed(1) : 0;
          const recommande = data.filter(a => a.recommande).length;
          const dist = [0, 0, 0, 0, 0];
          data.forEach(a => { if (a.note >= 1 && a.note <= 5) dist[a.note - 1]++; });
          setStats({ avg, total, recommande, distribution: dist });
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const submitReply = async (avisId) => {
    if (!replyText.trim()) return;
    try {
      await supabase.from("avis_prestataires").update({ reponse_pro: replyText.trim(), reponse_date: new Date().toISOString() }).eq("id", avisId);
      setReplyingTo(null);
      setReplyText("");
      loadAvis();
    } catch (e) { console.error(e); }
  };

  if (loading) return <div style={{ ...card, textAlign: "center", padding: "40px" }}><div style={{ color: C.textLight }}>Chargement...</div></div>;

  return (
    <div>
      {/* STATS */}
      {stats.total > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16, marginBottom: 16 }}>
          {/* Score global */}
          <div style={{ ...card, textAlign: "center", marginBottom: 0 }}>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 48, color: C.text, lineHeight: 1 }}>{stats.avg}</div>
            <div style={{ margin: "8px 0" }}><Stars count={Math.round(stats.avg)} size={20} /></div>
            <div style={{ fontSize: 13, color: C.textLight }}>{stats.total} avis</div>
            <div style={{ fontSize: 12, color: C.green, marginTop: 6 }}>👍 {stats.recommande}/{stats.total} recommandent</div>
          </div>

          {/* Distribution */}
          <div style={{ ...card, marginBottom: 0 }}>
            <div style={{ fontSize: 11, color: C.textLight, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 14, fontWeight: 600 }}>Distribution des notes</div>
            {[5, 4, 3, 2, 1].map(n => {
              const count = stats.distribution[n - 1];
              const pct = stats.total > 0 ? (count / stats.total) * 100 : 0;
              return (
                <div key={n} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: C.text, fontWeight: 600, width: 20, textAlign: "right" }}>{n}★</span>
                  <div style={{ flex: 1, height: 8, background: C.progressBg, borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: n >= 4 ? C.green : n === 3 ? C.amber : C.primary, borderRadius: 4, transition: "width 0.5s" }} />
                  </div>
                  <span style={{ fontSize: 11, color: C.textLight, width: 24 }}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* LISTE DES AVIS */}
      {avis.length > 0 ? (
        <div style={card}>
          <div style={{ fontSize: 11, color: C.textLight, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 18, fontWeight: 600 }}>Avis reçus ({avis.length})</div>
          {avis.map(a => (
            <div key={a.id} style={{ padding: "18px 0", borderBottom: `1px solid ${C.cardBorder}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.primaryBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: C.primary, fontWeight: 700 }}>
                    {a.couple_name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{a.couple_name || "Couple anonyme"}</div>
                    <div style={{ fontSize: 11, color: C.textLight }}><TimeAgo date={a.created_at} />{a.date_mariage && ` · Mariage ${new Date(a.date_mariage).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}`}</div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Stars count={a.note} size={14} />
                  {a.recommande && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 100, background: C.greenBg, color: C.green, fontWeight: 500 }}>👍</span>}
                </div>
              </div>

              {a.titre && <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4 }}>{a.titre}</div>}
              <p style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.6, marginBottom: 8 }}>{a.commentaire}</p>

              {/* Réponse existante */}
              {a.reponse_pro && (
                <div style={{ padding: "10px 14px", background: C.notesBg, borderRadius: 10, borderLeft: `3px solid ${C.primary}`, marginTop: 8 }}>
                  <div style={{ fontSize: 11, color: C.primary, fontWeight: 600, marginBottom: 4 }}>Votre réponse</div>
                  <p style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{a.reponse_pro}</p>
                </div>
              )}

              {/* Bouton répondre */}
              {!a.reponse_pro && replyingTo !== a.id && (
                <button onClick={() => { setReplyingTo(a.id); setReplyText(""); }}
                  style={{ marginTop: 8, background: "none", border: "none", color: C.primary, fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                  💬 Répondre à cet avis
                </button>
              )}

              {/* Formulaire réponse */}
              {replyingTo === a.id && (
                <div style={{ marginTop: 10 }}>
                  <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Merci pour votre retour ! Nous sommes ravis..."
                    style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${C.cardBorder}`, background: C.card, color: C.text, fontSize: 13, outline: "none", resize: "vertical", minHeight: 70, boxSizing: "border-box" }}
                    onFocus={e => e.target.style.borderColor = C.primary} onBlur={e => e.target.style.borderColor = C.cardBorder} />
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button onClick={() => submitReply(a.id)} disabled={!replyText.trim()}
                      style={{ padding: "8px 16px", background: replyText.trim() ? C.primary : C.cardBorder, color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: replyText.trim() ? "pointer" : "default" }}>
                      Publier la réponse
                    </button>
                    <button onClick={() => setReplyingTo(null)}
                      style={{ padding: "8px 16px", background: "transparent", border: `1px solid ${C.cardBorder}`, borderRadius: 8, fontSize: 12, color: C.textMuted, cursor: "pointer" }}>
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ ...card, textAlign: "center", padding: "60px 40px" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⭐</div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: C.text, marginBottom: 10 }}>Aucun avis pour l'instant</div>
          <p style={{ fontSize: 14, color: C.textLight, maxWidth: 400, margin: "0 auto" }}>Les couples pourront laisser un avis après avoir échangé avec vous. Un bon profil et des réponses rapides augmentent vos chances !</p>
        </div>
      )}
    </div>
  );
}